// Mode "Sécurisé lisible" — génération 100% navigateur (Zero-Knowledge).
// Format EXACT : [MOT][SPECIAL][9 CHIFFRES][SPECIAL][2 LETTRES a-zA-Z]  (ex. niChe+583104927/qR).
// Toutes les décisions aléatoires utilisent le CSPRNG (secureRandomInt -> crypto.getRandomValues).
// La wordlist unique SecurePass (fr) porte les mots ; le NOMBRE de mots (1 ici) appartient à ce
// mode, pas à la wordlist (spec §4).

import { getWordlist, getWordlistForLanguage, AVAILABLE_WORDLISTS } from "../wordlists/registry";
import { secureRandomInt } from "./passwords";

// Ensemble des caractères spéciaux lisibles/dictables : exactement 9 (spec §5B).
export const READABLE_SPECIALS = "!#$%&+?@/";
// 2 lettres finales : chacune parmi 52 possibilités équiprobables (a-z, A-Z).
export const READABLE_TAIL_LETTERS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

// Wordlist résolue dynamiquement depuis la langue (registre des langues -> wordlistLocale).
// `lang` absent => langue par défaut du registre. Aucune langue codée en dur ici.
export const readableWordsFor = (lang) => getWordlistForLanguage(lang).words;
export const readableWords = readableWordsFor();

// Met exactement UNE lettre en majuscule à une position uniforme du mot (toutes positions possibles,
// y compris la première). Le mot arrive en lowercase.
export function capitalizeRandom(word) {
  const pos = secureRandomInt(word.length);
  return word.slice(0, pos) + word.charAt(pos).toUpperCase() + word.slice(pos + 1);
}

function pickWord(lang) {
  const words = readableWordsFor(lang);
  return words[secureRandomInt(words.length)];
}

// Génère un mot de passe "Sécurisé lisible". Retourne { password, wordLength } car l'entropie
// dépend de la longueur réelle du mot tiré.
export function generateReadablePassword(lang) {
  const word = capitalizeRandom(pickWord(lang));
  const sp1 = READABLE_SPECIALS[secureRandomInt(READABLE_SPECIALS.length)];
  const digits = Array.from({ length: 9 }, () => String(secureRandomInt(10))).join("");
  const sp2 = READABLE_SPECIALS[secureRandomInt(READABLE_SPECIALS.length)];
  const tail = Array.from({ length: 2 }, () => READABLE_TAIL_LETTERS[secureRandomInt(READABLE_TAIL_LETTERS.length)]).join("");
  return { password: `${word}${sp1}${digits}${sp2}${tail}`, wordLength: word.length };
}

// Entropie EXACTE du processus (spec §7) :
//   log2(N) + log2(L) + 9*log2(10) + 2*log2(S) + 2*log2(52)
// avec N = 4096 mots, S = 9 caractères spéciaux, L = longueur du mot choisi.
export function readableEntropyBits(wordLength = 6, lang) {
  const N = readableWordsFor(lang).length;
  const S = READABLE_SPECIALS.length;
  const bits = Math.log2(N) + Math.log2(wordLength) + 9 * Math.log2(10) + 2 * Math.log2(S) + 2 * Math.log2(READABLE_TAIL_LETTERS.length);
  return Math.round(bits);
}

// Option "Inclure un mot lisible" du mode Aléatoire : AJOUTE le bloc fixe "-mot-" (1 majuscule
// aléatoire) à une position aléatoire, sans retirer aucun caractère aléatoire. Les deux tirets sont
// des séparateurs fixes (présents même sans symboles) et ne comptent pas dans l'entropie.
export function insertReadableWord(base, lang) {
  const word = capitalizeRandom(pickWord(lang));
  const pos = secureRandomInt(base.length + 1); // 0..base.length inclus
  return { password: `${base.slice(0, pos)}-${word}-${base.slice(pos)}`, wordLength: word.length };
}

// Estimation d'entropie CONSCIENTE DE LA STRUCTURE pour le mode Sécurisé lisible (et toute chaîne
// saisie/éditée dans ce mode). Un bloc de lettres vaut AU PLUS « un mot » (12 + log2(longueur) pour
// la majuscule), jamais N lettres aléatoires : on ne surévalue donc jamais un mot connu, et supprimer
// un caractère ne fait jamais MONTER la valeur de façon trompeuse. Cohérent avec readableEntropyBits
// sur la forme canonique [MOT][SPECIAL][9 chiffres][SPECIAL][2 lettres].
// Union de toutes les wordlists SecurePass enregistrées (toutes 4096 mots => même entropie).
const READABLE_WORD_SET = new Set(AVAILABLE_WORDLISTS.flatMap((m) => getWordlist(m).words));
const CANONICAL_RE = /^([A-Za-z]+)[!#$%&+?@/]\d{9}[!#$%&+?@/][A-Za-z]{2}$/;

export function estimateReadableBits(pw) {
  if (!pw) return 0;
  // Forme canonique exacte (mot du dictionnaire, 1 majuscule) : entropie réelle du mécanisme.
  const m = pw.match(CANONICAL_RE);
  if (m && READABLE_WORD_SET.has(m[1].toLowerCase()) && (m[1].match(/[A-Z]/g) || []).length === 1) {
    return readableEntropyBits(m[1].length);
  }
  const runs = pw.match(/[A-Za-z]+|[0-9]+|[^A-Za-z0-9]+/g) || [];
  let bits = 0;
  for (const run of runs) {
    if (/^[A-Za-z]+$/.test(run)) {
      const pool = /[A-Z]/.test(run) && /[a-z]/.test(run) ? 52 : 26;
      // min() : un bloc de lettres est compté comme un mot (conservateur), jamais plus.
      bits += Math.min(12 + Math.log2(run.length), run.length * Math.log2(pool));
    } else if (/^[0-9]+$/.test(run)) {
      bits += run.length * Math.log2(10);
    } else {
      for (const c of run) bits += Math.log2(READABLE_SPECIALS.includes(c) ? READABLE_SPECIALS.length : 32);
    }
  }
  return Math.round(bits);
}

// Supplément d'entropie CONSERVATEUR pour "Inclure un mot lisible" (spec §12) :
//   + log2(4096) (= 12, choix du mot) + log2(longueur du mot) (position de la majuscule).
// La position d'insertion n'est volontairement PAS comptée.
export function readableWordEntropyAddon(wordLength) {
  return 12 + Math.log2(wordLength);
}

// Entropie de génération EXACTE, uniquement si la valeur correspond strictement au format
// « Sécurisé & lisible » avec un mot d'une wordlist SecurePass enregistrée. Sinon null.
export function exactReadableEntropy(pw, preferLocale) {
  const m = String(pw || "").match(CANONICAL_RE);
  if (!m || (m[1].match(/[A-Z]/g) || []).length !== 1) return null;
  const word = m[1].toLowerCase();
  // Un mot présent dans plusieurs wordlists est attribué en priorité à la langue active (même entropie).
  const ordered = [...AVAILABLE_WORDLISTS].sort((a, b) => (b.locale === preferLocale) - (a.locale === preferLocale));
  const list = ordered.find((meta) => getWordlist(meta).words.includes(word));
  if (!list) return null;
  return { bits: readableEntropyBits(word.length, list.locale), locale: list.locale };
}

